import React from 'react'
import { AgGridReact } from 'ag-grid-react'
import { ModuleRegistry, AllCommunityModule, themeQuartz } from 'ag-grid-community'
import Layout from '../components/Layout'
import { capitalizeMakeName, formatModelName } from '../utils/formatters'
import { useABBData } from '../hooks/abb/useABBData'
import { useABBGrid } from '../hooks/abb/useABBGrid'

// Register AG-Grid modules
ModuleRegistry.registerModules([AllCommunityModule])

// Create custom theme
const customTheme = themeQuartz.withParams({
  accentColor: '#fdba74',
  fontFamily: 'Inter',
  headerBackgroundColor: '#3b0764',
  headerTextColor: '#e9d5ff',
  headerFontWeight: 600
})

export default function ABB() {
  const {
    modelId, navigate, modelInfo, loading, error,
    hasAccess, checkingAccess, marketSettings,
    abbData, rowData, selectedYearModel, setSelectedYearModel,
    yearModels, fetchABBData,
  } = useABBData()

  const {
    saving, pendingChanges, batchSaveStatus, contextMenu,
    gridApiRef, contextMenuRef, columnDefs,
    handleCellValueChanged, handleSaveChanges, handleDiscardChanges,
    handleContextMenu, handleContextMenuPaste,
  } = useABBGrid({ modelId, selectedYearModel, abbData, marketSettings, fetchABBData })

  if (checkingAccess || loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-lg text-gray-600">
            {checkingAccess ? 'Checking permissions...' : 'Loading value data...'}
          </div>
        </div>
      </Layout>
    )
  }

  if (!hasAccess || error || !modelInfo) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="text-lg text-red-600 mb-4">
              {error || 'You do not have permission to access this page'}
            </div>
            <button
              onClick={() => navigate(`/market/${modelId}/configure`)}
              className="text-sm text-gray-600 hover:text-gray-900 inline-flex items-center"
            >
              ← Back to Market Configuration
            </button>
          </div>
        </div>
      </Layout>
    )
  }

  if (!marketSettings || !marketSettings.production_start) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="text-lg text-amber-600 mb-4">
              Production years not configured
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Please configure production start and end years in Market Parameters first.
            </p>
            <button
              onClick={() => navigate(`/market/${modelId}/configure`)}
              className="text-sm text-gray-600 hover:text-gray-900 inline-flex items-center"
            >
              ← Back to Market Configuration
            </button>
          </div>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        <div className="w-full px-2 sm:px-4 lg:px-6 xl:px-8 py-6">
          <div className="w-full max-w-full">
            <div className="mb-6">
              <button
                onClick={() => navigate(`/market/${modelId}/configure`)}
                className="text-sm text-gray-600 hover:text-gray-900 mb-4 inline-flex items-center"
              >
                ← Back to Market Configuration
              </button>
              <h1 className="text-3xl font-bold text-purple-950">
                Aircraft Value Tracker: {capitalizeMakeName(modelInfo.make_name)} {formatModelName(modelInfo.model_name)}
              </h1>
              <p className="mt-2 text-sm text-gray-600">
                Select a year model below, then enter quarterly values for each year. Click on any cell to edit, or paste data from Excel.
              </p>
              {batchSaveStatus && (
                <p className={`mt-2 text-sm ${batchSaveStatus?.includes('Error') ? 'text-red-600' : batchSaveStatus?.includes('successfully') ? 'text-green-600' : 'text-blue-600'}`}>
                  {batchSaveStatus}
                </p>
              )}

              {/* Save/Discard buttons - shown when there are pending changes */}
              {pendingChanges.length > 0 && (
                <div className="mt-4 flex items-center gap-4">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleSaveChanges}
                      disabled={saving}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150 flex items-center gap-2"
                    >
                      {saving ? (
                        <>
                          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Saving...
                        </>
                      ) : (
                        <>
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Save {pendingChanges.length} Change{pendingChanges.length > 1 ? 's' : ''}
                        </>
                      )}
                    </button>
                    <button
                      onClick={handleDiscardChanges}
                      disabled={saving}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
                    >
                      Discard
                    </button>
                  </div>
                  <span className="text-sm text-amber-600">
                    {pendingChanges.length} unsaved change{pendingChanges.length > 1 ? 's' : ''}
                  </span>
                </div>
              )}
            </div>

            {/* Year Model Pills */}
            <div className="mb-6">
              <div className="flex flex-wrap gap-2">
                {yearModels.map((yearModel) => (
                  <button
                    key={yearModel}
                    onClick={() => setSelectedYearModel(yearModel)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-150 ${
                      selectedYearModel === yearModel
                        ? 'bg-purple-950 text-white'
                        : 'bg-purple-100 text-purple-800 hover:bg-purple-200'
                    }`}
                  >
                    {yearModel}
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-4">
              <div style={{ width: '100%' }} onContextMenu={handleContextMenu}>
                <AgGridReact
                  rowData={rowData}
                  columnDefs={columnDefs}
                  defaultColDef={{
                    resizable: true
                  }}
                  onCellValueChanged={handleCellValueChanged}
                  onGridReady={(params) => { gridApiRef.current = params.api }}
                  animateRows={true}
                  stopEditingWhenCellsLoseFocus={true}
                  theme={customTheme}
                  domLayout="autoHeight"
                />
              </div>
              <div className="mt-4 text-sm text-gray-600">
                <p>Currently editing values for: <span className="font-semibold">{selectedYearModel} Year Model</span></p>
                <p className="mt-1">Production Years: {marketSettings.production_start} - {marketSettings.production_end || 'Present'}</p>
                <p className="mt-2">Click any value cell to edit, or right-click / paste (Ctrl+V / Cmd+V) from Excel. Changes are saved automatically.</p>
              </div>
            </div>

            {/* Context Menu for Right-Click Paste */}
            {contextMenu && (
              <div
                ref={contextMenuRef}
                className="fixed bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50"
                style={{ left: contextMenu.x, top: contextMenu.y }}
              >
                <button
                  onClick={handleContextMenuPaste}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-900 flex items-center gap-2"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  Paste
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  )
}